import React from 'react';

/**
 * "Powered by Amutech" branding on every public landing / form / campaign page.
 * Links to the marketing site.
 *
 * Responsive:
 * - Desktop (≥640px): a compact pill pinned top-left with logo + "מופעל ע״י
 *   Amutech" — unchanged, stays out of the hero.
 * - Mobile (<640px): a full-width sticky footer bar pinned to the bottom with
 *   the logo + "מופעל ע״י Amutech · מערכת לניהול עמותות". The global
 *   AccessibilityWidget FAB floats just above this bar (see .a11y-fab).
 */
const PoweredByAmutech: React.FC = () => (
  <>
    <style>{`
      /* ── Desktop full-width top app bar ────────────────────────────
         A sticky, full-width bar pinned to the top of the page. It sits in
         normal flow so it PUSHES the page content down (never overlaps the
         hero), and stays pinned while scrolling. A thin blue accent line
         tops it, the Amutech logo + tagline sit on the start (right) edge,
         and a "create your own campaign" CTA on the end (left) edge. */
      .amutech-topbar {
        position: sticky;
        top: 0;
        z-index: 9999;
        background: #fff;
      }
      .amutech-topbar__accent {
        height: 3px;
        background: linear-gradient(90deg, #1565C0, #29B6F6, #1565C0);
      }
      .amutech-topbar__bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 7px 20px;
        background: #fff;
        border-bottom: 1px solid rgba(0, 0, 0, 0.07);
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
        text-decoration: none;
        direction: rtl;
        font-family: Heebo, Arial, sans-serif;
      }
      .amutech-topbar__brand { display: flex; align-items: center; gap: 11px; }
      /* Icon-only mark (amutech-icon.png) — already cropped to just the emblem,
         no "AMUTECH" wordmark, so it renders straight with no clip trick. */
      .amutech-topbar__logo {
        height: 46px; width: auto; max-width: none;
        object-fit: contain; display: block; flex-shrink: 0;
      }
      .amutech-topbar__text {
        display: flex; flex-direction: column; line-height: 1.22; text-align: right;
      }
      .amutech-topbar__text .l1 { font-size: 14px; font-weight: 600; color: #4b5563; }
      .amutech-topbar__text .l1 strong { color: #1565C0; }
      .amutech-topbar__text .l2 { font-size: 11.5px; font-weight: 500; color: #9ca3af; }
      .amutech-topbar__cta {
        display: flex; align-items: center; gap: 5px; white-space: nowrap;
        font-size: 13px; font-weight: 600; color: #1565C0;
      }
      .amutech-topbar__cta svg { display: block; }

      /* ── Mobile sticky footer bar ──────────────────────────────── */
      .amutech-bar { display: none; }

      @media (max-width: 640px) {
        /* hide the desktop top bar, show the full-width sticky footer bar */
        .amutech-topbar { display: none; }

        .amutech-bar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          /* tight vertical padding so the logo stays prominent without
             making the bar tall. */
          padding: 2px 14px;
          padding-bottom: max(2px, env(safe-area-inset-bottom));
          background: rgba(255, 255, 255, 0.94);
          -webkit-backdrop-filter: blur(8px);
          backdrop-filter: blur(8px);
          border-top: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
          text-decoration: none;
          direction: rtl;
          font-family: Heebo, Arial, sans-serif;
        }
        /* Mobile: crop the "AMUTECH" wordmark off the bottom of the logo so
           only the icon mark shows (the bar already spells out "Amutech" in
           text). The PNG is ~67% icon / bottom ~33% wordmark, so a fixed-height
           clip window that's ~2/3 of the rendered image height hides the text. */
        .amutech-bar__logo {
          display: block;
          position: relative;
          width: 48px;   /* tight square crop → trims side whitespace so the
                            icon sits snug next to the text */
          height: 46px;  /* shows ~2/3 of the image height → wordmark cropped */
          overflow: hidden;
          flex-shrink: 0;
          transform: translateY(-4px);  /* nudge the icon up a touch */
        }
        .amutech-bar__logo img {
          height: 69px;
          width: 69px;
          max-width: none;   /* override Tailwind preflight's max-width:100% */
          object-fit: contain;
          display: block;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);   /* center the icon horizontally */
        }
        .amutech-bar__text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
          text-align: right;
        }
        .amutech-bar__text .l1 { font-size: 12.5px; font-weight: 600; color: #4b5563; }
        .amutech-bar__text .l1 strong { color: #1565C0; }
        .amutech-bar__text .l2 { font-size: 11px; font-weight: 500; color: #9ca3af; }
      }
    `}</style>

    {/* Desktop: full-width sticky top app bar */}
    <div className="amutech-topbar">
      <div className="amutech-topbar__accent" />
      <a
        href="https://amutech.co.il"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="מופעל ע״י Amutech — מערכת לניהול עמותות וקמפיינים"
        className="amutech-topbar__bar"
      >
        <span className="amutech-topbar__brand">
          <img src="/amutech-icon.png" alt="" className="amutech-topbar__logo" />
          <span className="amutech-topbar__text">
            <span className="l1">מופעל ע״י <strong>Amutech</strong></span>
            <span className="l2">מערכת לניהול עמותות וקמפיינים</span>
          </span>
        </span>
        <span className="amutech-topbar__cta">
          הקימו קמפיין משלכם
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </span>
      </a>
    </div>

    {/* Mobile: full-width sticky footer bar */}
    <a
      href="https://amutech.co.il"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="מופעל ע״י Amutech — מערכת לניהול עמותות וקמפיינים"
      className="amutech-bar"
    >
      <span className="amutech-bar__logo">
        <img src="/amutech-logo.png" alt="" />
      </span>
      <span className="amutech-bar__text">
        <span className="l1">מופעל ע״י <strong>Amutech</strong></span>
        <span className="l2">מערכת לניהול עמותות וקמפיינים</span>
      </span>
    </a>
  </>
);

export default PoweredByAmutech;
